import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { getFeaturedComics, getLatestComics, getHotComics, getAllComics, formatPopularity } from '@/lib/comics';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import CategoryGrid from '@/pages/home/components/CategoryGrid';
import ComicSection from '@/pages/home/components/ComicSection';
import CollectionSection from '@/pages/home/components/CollectionSection';

export default function Home() {
  useSEO({
    title: '韩漫-最新韩漫漫画资源与热门推荐平台',
    description: '韩漫网站专注于提供丰富的韩漫漫画资源，涵盖各类热门题材和作品，带给用户多样化的阅读选择和漫画推荐体验。',
    keywords: '韩漫,韩漫漫画,韩国漫画,漫画资源,热门韩漫,漫画推荐,漫画阅读,韩漫更新',
    path: '/',
  });

  const featured = getFeaturedComics();
  const latest = getLatestComics(8);
  const hot = getHotComics(8);
  const total = getAllComics().length;

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 pt-6 md:pt-8 flex flex-col gap-12 md:gap-16">
        <HeroCarousel comics={featured} />

        <CategoryGrid />

        <ComicSection
          title="热门更新"
          subtitle="最近更新最多人追的连载韩漫"
          moreTo="/category/romance"
          moreLabel="更多分类"
          comics={latest}
          showAuthor
        />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8 items-start">
          <ComicSection
            title="人气榜单"
            subtitle="按热度排列，大家都在看"
            comics={hot.slice(0, 8)}
          />
          <aside className="rounded-lg border border-background-200 bg-background-100 p-5">
            <h3 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2">
              <i className="ri-bar-chart-box-line text-primary-500"></i>
              平台数据
            </h3>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-background-50 border border-background-200 p-3">
                <p className="font-heading font-extrabold text-2xl text-primary-600">{total}</p>
                <p className="mt-1 text-xs text-foreground-600">在架漫画</p>
              </div>
              <div className="rounded-lg bg-background-50 border border-background-200 p-3">
                <p className="font-heading font-extrabold text-2xl text-accent-600">8</p>
                <p className="mt-1 text-xs text-foreground-600">题材分类</p>
              </div>
              <div className="rounded-lg bg-background-50 border border-background-200 p-3">
                <p className="font-heading font-extrabold text-2xl text-primary-600">{formatPopularity(982000)}</p>
                <p className="mt-1 text-xs text-foreground-600">最高热度</p>
              </div>
              <div className="rounded-lg bg-background-50 border border-background-200 p-3">
                <p className="font-heading font-extrabold text-2xl text-accent-600">4.9</p>
                <p className="mt-1 text-xs text-foreground-600">平均评分</p>
              </div>
            </div>
            <Link
              to="/about"
              className="mt-4 inline-flex w-full items-center justify-center gap-1 h-10 rounded-md bg-secondary-500 text-background-50 text-sm font-label font-medium hover:bg-secondary-600 transition-colors cursor-pointer"
            >
              了解韩漫平台
              <i className="ri-arrow-right-line"></i>
            </Link>
          </aside>
        </div>

        <CollectionSection />

        <section className="w-full rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 md:gap-10 items-center">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                关于「韩漫」漫画聚合平台
              </h2>
              <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">
                韩漫聚焦韩国漫画资源的聚合与推荐，覆盖爱情、奇幻、校园、都市、悬疑、热血、古风、治愈八大题材。
                我们通过清晰的分类、强大的站内搜索与个性化推荐，帮助每一位读者更快地发现值得一追的好作品。
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-1 px-5 h-10 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  了解我们
                </Link>
                <Link
                  to="/search?q=爱情"
                  className="inline-flex items-center gap-1 px-5 h-10 rounded-md border border-primary-300 text-primary-700 text-sm font-label font-medium hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  试试搜索
                </Link>
              </div>
            </div>
            <div className="w-full h-[220px] rounded-lg overflow-hidden border border-primary-100">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20style%20illustration%20of%20a%20cozy%20reading%20nook%20with%20tablet%20showing%20colorful%20comic%20panels%2C%20warm%20coral%20orange%20and%20fresh%20teal%20tones%2C%20clean%20modern%20background%2C%20soft%20cel%20shading%2C%20welcoming%20platform%20promo%20art&width=900&height=620&seq=hanman-about-band&orientation=landscape"
                alt="韩漫平台阅读场景插画"
                title="韩漫漫画在线阅读平台"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}